import {
  loadAuthHeaders,
  saveAuthHeaders,
  clearAuthHeaders,
  type AuthHeaders,
} from './auth-storage';

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly code: string | null = null,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export class UnauthorizedError extends ApiError {
  constructor(message = 'unauthorized', code: string | null = null) {
    super(401, message, code);
    this.name = 'UnauthorizedError';
  }
}

export interface ApiResponse<T> {
  body: T;
  headers: Partial<AuthHeaders>;
}

export interface ApiRequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  signal?: AbortSignal;
}

const TOKEN_HEADER_KEYS: readonly (keyof AuthHeaders)[] = [
  'access-token',
  'client',
  'uid',
  'expiry',
];

// Backend base URL. In dev `VITE_API_URL` is unset, so paths stay relative
// and Vite's `/api`, `/loot_machine`, `/rails` proxy entries (see
// vite.config.ts) forward them to the local Rails server. In prod the env
// var is injected at build time (`https://api.getcardbase.com` or
// `https://staging-api.getcardbase.com`) and every fetch becomes absolute.
const API_BASE = (import.meta.env.VITE_API_URL ?? '').replace(/\/$/, '');

function readTokenHeaders(res: Response): Partial<AuthHeaders> {
  const out: Partial<AuthHeaders> = {};
  for (const k of TOKEN_HEADER_KEYS) {
    const v = res.headers.get(k);
    if (v) out[k] = v;
  }
  return out;
}

function persistRotatedHeaders(fromResponse: Partial<AuthHeaders>): void {
  // Backend sends a fresh access-token only when it rotated. If the header
  // is missing, we keep what we already have. If it's present, replace the
  // full bundle (devise_token_auth sends all four together when rotating).
  if (!fromResponse['access-token']) return;
  const current = loadAuthHeaders();
  if (!current) {
    // First-time set (e.g. login response). Require all four to land.
    if (
      fromResponse.client &&
      fromResponse.uid &&
      fromResponse.expiry
    ) {
      saveAuthHeaders({
        'access-token': fromResponse['access-token'],
        client: fromResponse.client,
        uid: fromResponse.uid,
        expiry: fromResponse.expiry,
      });
    }
    return;
  }
  saveAuthHeaders({
    'access-token': fromResponse['access-token'],
    client: fromResponse.client ?? current.client,
    uid: fromResponse.uid ?? current.uid,
    expiry: fromResponse.expiry ?? current.expiry,
  });
}

const isString = (m: unknown): m is string => typeof m === 'string';

// A Rails model's errors.as_json ({ field: [messages] }), worded like its full_messages
function fieldErrorMessages(errors: Record<string, unknown>): string[] {
  return Object.entries(errors).flatMap(([field, messages]) =>
    (Array.isArray(messages) ? messages.filter(isString) : []).map((m) =>
      field === 'base' || m.startsWith(field)
        ? m
        : `${field.charAt(0).toUpperCase()}${field.slice(1).replace(/_/g, ' ')} ${m}`,
    ),
  );
}

async function parseError(res: Response): Promise<{ message: string; code: string | null }> {
  try {
    const j = (await res.json()) as {
      errors?: string[] | Record<string, unknown> | null;
      message?: string;
      error?: string;
      error_code?: string;
    };
    const code = j.error_code ?? (typeof j.error === 'string' ? j.error : null);
    if (Array.isArray(j.errors) && j.errors.length) return { message: j.errors.join(', '), code };
    if (j.errors && typeof j.errors === 'object' && !Array.isArray(j.errors)) {
      const full = j.errors['full_messages'];
      const messages = Array.isArray(full) ? full.filter(isString) : fieldErrorMessages(j.errors);
      if (messages.length) return { message: messages.join(', '), code };
    }
    if (j.message) return { message: j.message, code };
    return { message: `HTTP ${res.status}`, code };
  } catch {
    return { message: `HTTP ${res.status}`, code: null };
  }
}

function authHeaders(): Headers {
  const stored = loadAuthHeaders();
  const headers = new Headers();
  if (stored) {
    headers.set('access-token', stored['access-token']);
    headers.set('client', stored.client);
    headers.set('uid', stored.uid);
    headers.set('expiry', stored.expiry);
  }
  return headers;
}

function apiUrl(path: string): string {
  return path.startsWith('http') ? path : `${API_BASE}${path}`;
}

// A 401 drops the stored session; any other failure carries the backend's
// message. Rotated tokens are kept only from a successful response.
async function ensureOk(res: Response): Promise<Partial<AuthHeaders>> {
  const rotated = readTokenHeaders(res);
  if (res.status === 401) {
    clearAuthHeaders();
    const { message, code } = await parseError(res);
    throw new UnauthorizedError(message, code);
  }
  if (!res.ok) {
    const { message, code } = await parseError(res);
    throw new ApiError(res.status, message, code);
  }

  persistRotatedHeaders(rotated);
  return rotated;
}

export async function apiRequest<T = unknown>(
  path: string,
  opts: ApiRequestOptions = {},
): Promise<ApiResponse<T>> {
  const headers = authHeaders();
  headers.set('content-type', 'application/json');

  const init: RequestInit = { method: opts.method ?? 'GET', headers };
  if (opts.signal) init.signal = opts.signal;
  if (opts.body !== undefined) {
    if (opts.body instanceof FormData) {
      init.body = opts.body;
      headers.delete('content-type'); // browser sets multipart boundary
    } else {
      init.body = JSON.stringify(opts.body);
    }
  }

  const res = await fetch(apiUrl(path), init);
  const rotated = await ensureOk(res);

  const ct = res.headers.get('content-type') ?? '';
  const body = /\bjson\b/i.test(ct) ? ((await res.json()) as T) : (undefined as T);
  return { body, headers: rotated };
}

// File endpoints (CSV exports). The session lives in headers, not cookies, so
// a plain link would arrive unauthenticated: fetch it and hand back the Blob
// for the caller to save. Errors still come back as JSON.
export async function apiDownload(
  path: string,
  opts: Pick<ApiRequestOptions, 'signal'> = {},
): Promise<Blob> {
  const init: RequestInit = { method: 'GET', headers: authHeaders() };
  if (opts.signal) init.signal = opts.signal;

  const res = await fetch(apiUrl(path), init);
  await ensureOk(res);
  return res.blob();
}
